/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  Tooltip,
  Title,
  ActionIcon,
  Text,
  Drawer,
  useMantineColorScheme,
  alpha,
  isLightColor,
} from "@mantine/core"
import {
  IconArrowsDoubleSwNe,
  IconChevronRight,
  IconChevronDown,
} from "@tabler/icons-react"
import {
  CSSProperties,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { DateOnly, Nullable, TimeOnly } from "../../../../@client/System"
// import { CreateShiftModal } from "./modals/CreateShiftModal"
// import { EditShiftModal } from "./modals/EditShiftModal"
import { match } from "ts-pattern"
import {
  SuspenseWithErrorBoundary,
  SuspenseWithFallback,
} from "../../../../-components/suspense"
import { ShiftTypeDto } from "../../../../@client/Theasoft_ApiServer_ShiftTypes"
import { ts_Schicht } from "../../../../@client/Theasoft_DataClasses_Perso"
import { TFunction } from "i18next"
import dayjs from "dayjs"
import { MenuCellRenderer } from "./cells/menuCellRenderer"
import { useShallow } from "zustand/react/shallow"
import { useGridStore } from "./state"
import { EditAbsenceContent } from "@/routes/app/duties/orchestra/-components/modals/edit-absence-modal"
import { AddCastModal } from "@/routes/app/duties/orchestra/-components/modals/add-cast-modal"
import { EditCastModal } from "@/routes/app/duties/orchestra/-components/modals/edit-cast-modal"
import { AddAbsenceModal } from "@/routes/app/duties/orchestra/-components/modals/add-absence-modal"
import { OrchestraActivityDto } from "@/@client/Theasoft_ApiServer_Orchestra"
import { ActivityHeaderCell } from "../../duties/orchestra/-components/cells/activity-header-cell"
import { useSelectedDepartmentIds } from "../../duties/-components/useSelectedDepartmentIds"
import styles from "./cells/styles.module.css"
import clsx from "clsx"
import { AddPersonStatusShiftModalContent } from "../../duties/orchestra/-components/modals/add-personstatus-shift"
import {
  DepartmentDutyRosterResult,
  PersonStatusDto,
  DutySchedulingRowHeader as RowHeader,
} from "@/orval/gen/model"
import { FormulaCell } from "./cells/formula-cell"
import { FormulaHeaderCell } from "./cells/formula-header-cell"
import { useVirtualizer } from "@tanstack/react-virtual"
import { CreateOrEditShiftForm } from "./modals/CreateOrEditShiftForm"
import { useDeleteShift, useDepartmentDutyRosterAddShift } from "@/orval/gen"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { usePostMutation } from "../../../../typed-client/hooks/use-post-mutation"
import { useUserLocalStorage } from "@/routes/app/-components/use-user-local-storage"

export type CellId = `[${string}][${string}/${number}]`

function getCellId(hierarchicalRowId: string, day: string, colId: number) {
  return `[${hierarchicalRowId}][${day}/${colId}]` as CellId
}

const getRowId = (cellId: CellId) => cellId.split("]")[0].substring(1)

const getColId = (cellId: CellId) => cellId.split("]")[1].substring(1)

function getContractId(cellId: CellId) {
  return parseInt(getRowId(cellId).split("/")[2])
}

function getDay(cellId: CellId): DateOnly {
  return getColId(cellId).split("/")[0] as DateOnly
}

const getDepartmentId = (cellId: CellId) => {
  const raw = getRowId(cellId).split("/")[0]
  return parseInt(raw)
}

const getWorkareaId = (cellId: CellId) => {
  const raw = getRowId(cellId).split("/")[1]
  return parseInt(raw)
}

function getShiftSlotId(shiftId: ts_Schicht): number {
  switch (shiftId) {
    case "Schicht1":
      return 1
    case "Schicht2":
      return 2
    case "Schicht3":
      return 3
  }
}

const cellSizes = { sm: [20, 20] } as const
const formulaTrackWidth = 25
const formulaTrackOffset = 110

type GridOrientation = "normal" | "transpose"

function isOnlyGroupingRow(header: RowHeader, siblings: RowHeader[]) {
  if (header.rowType !== "Department" && header.rowType !== "Workarea") {
    return false
  }

  return (
    siblings.filter((sibling) => sibling.rowType === header.rowType).length ===
    1
  )
}

function getGroupingTrackSpan(
  header: RowHeader,
  siblings: RowHeader[]
): number {
  const children = (header.children ?? []) as RowHeader[]
  return (
    (isOnlyGroupingRow(header, siblings) ? 0 : 1) +
    children.reduce(
      (span, child) => span + getGroupingTrackSpan(child, children),
      0
    )
  )
}

function orientGridStyle(
  orientation: GridOrientation,
  style: CSSProperties
): CSSProperties {
  if (orientation === "normal") {
    return style
  }

  const { gridColumn, gridRow, left, top, ...rest } = style
  return {
    ...rest,
    gridColumn: gridRow,
    gridRow: gridColumn,
    left: top,
    top: left,
  }
}

type AddShiftModalState = {
  view: "add-shift"
  cellId: CellId
  shiftType?: ShiftTypeDto
}

type EditShiftModalState = {
  view: "edit-shift"
  cellId: CellId
  dutyRosterId: number
  shiftId: ts_Schicht
}

type EditAbsenceShiftModalState = {
  view: "edit-absence-shift"
  cellId: CellId
  dutyRosterId: number
  shiftId: ts_Schicht
}

type AddAbsenceModalState = {
  view: "add-absence"
  cellId: CellId
  shiftType: ShiftTypeDto
}

type AddPersonStatusAbsenceModalState = {
  view: "add-personstatus-absence"
  cellId: CellId
  shiftType: ShiftTypeDto
}

type AddPersonStatusShiftModalState = {
  view: "add-personstatus-shift"
  cellId: CellId
  shiftTypeId: number
}

type EditPersonStatusAbsenceModalState = {
  view: "edit-personstatus-absence"
  cellId: CellId
  personStatus: PersonStatusDto
}

type EditAbsenceModalState = {
  view: "edit-absence"
  cellId: CellId
  personStatus: PersonStatusDto
}

type AddCast = {
  view: "add-cast"
  cellId: CellId
  roleId: number
}

type EditCast = {
  view: "edit-cast"
  cellId: CellId
  castId: number
}
// move to state

export type ModalState =
  | AddShiftModalState
  | EditShiftModalState
  | EditAbsenceShiftModalState
  | AddAbsenceModalState
  | EditAbsenceModalState
  | AddPersonStatusAbsenceModalState
  | AddPersonStatusShiftModalState
  | EditPersonStatusAbsenceModalState
  | AddCast
  | EditCast

type UseStateResult<S> = [S, Dispatch<SetStateAction<S>>]

export type DepartmentDutyRosterSpreadsheetContext = {
  t: TFunction<"app_server", undefined>
  activeMenuState: UseStateResult<CellId | null>
  mouseOverState: UseStateResult<CellId | null>
  modalState: UseStateResult<ModalState | null>
  setEditAbsence: (cellId: CellId, personStatusId: number) => void
  addShifts: (
    cellId: CellId,
    shiftType: ShiftTypeDto,
    startTime: Nullable<TimeOnly>,
    endTime: Nullable<TimeOnly>,
    note: string | null
  ) => void
  getActivity: (cellId: CellId) => OrchestraActivityDto | null
  setAddCast: (cellId: CellId, roleId: number) => void
  setEditCast: (cellId: CellId, castId: number) => void
}

export type QuickQueueStats = {
  visible: boolean
  pending: number
  total: number
  completed: number
  percent: number
  running: boolean
  cancelRequested: boolean
}

export function DepartmentDutyRosterSpreadsheet({
  focus,
  title,
  data: items,
  onRefetch,
  onQueueLoadingChange,
  onQueueStatsChange,
  onQueueCancelRegister,
  hoverEffect = true,
  departmentTreeNodes,
}: {
  focus: "shift-planning" | "cast-planning" | "absence-planning"
  title?: ReactNode
  data: DepartmentDutyRosterResult
  onRefetch?: () => void | Promise<unknown>
  onQueueLoadingChange?: (loading: boolean) => void
  onQueueStatsChange?: (stats: QuickQueueStats) => void
  onQueueCancelRegister?: (cancel: () => void) => void
  hoverEffect?: boolean
  departmentTreeNodes: string[]
}) {
  const [orientation, setOrientation] = useUserLocalStorage<GridOrientation>({
    key: "duty-scheduling-spreadsheet-orientation",
    defaultValue: "normal",
  })
  const quickAddShift = useDepartmentDutyRosterAddShift({
    mutation: {
      onError: showErrorNotification,
    },
  })
  const quickDeleteShift = useDeleteShift({
    mutation: {
      onError: showErrorNotification,
    },
  })
  const quickDeletePersonStatus = usePostMutation(
    "/api/Orchestra/DeleteAbsence"
  )

  const { rowExpandedStates, setRowExpandedStates, ...store } = useGridStore(
    useShallow((s) => ({
      headerColumnCount: s.headerColumnCount,
      formulas: s.formulas,
      setContext: s.setContext,
      columns: s.columns,
      gridTemplateColumns: s.gridTemplateColumns,
      window: s.window,
      rowExpandedStates: s.rowExpandedStates,
      setRowExpandedStates: s.setRowExpandedStates,
    }))
  )

  const { t } = useTranslation("app_server")
  const gridRef = useRef<HTMLDivElement>(null)
  const horizontalScrollRef = useRef<HTMLElement | null>(null)
  const verticalScrollRef = useRef<HTMLElement | null>(null)
  const rowExpandedStatesRef = useRef(rowExpandedStates)
  const quickAddQueueRef = useRef<Promise<void>>(Promise.resolve())
  const quickAddLatestTokenRef = useRef(0)
  const quickAddNeedsRefetchRef = useRef(false)
  const quickAddQueueLoadingRef = useRef(false)
  const quickQueuePendingCountRef = useRef(0)
  const quickQueueTotalCountRef = useRef(0)
  const quickQueueRunningRef = useRef(false)
  const quickQueueCancelRequestedRef = useRef(false)
  const queuedPersonStatusDeleteIdsRef = useRef(new Set<number>())
  const [, forceQueueStatsRender] = useState(0)

  const setQueueLoading = useCallback(
    (loading: boolean) => {
      if (quickAddQueueLoadingRef.current === loading) {
        return
      }

      quickAddQueueLoadingRef.current = loading
      onQueueLoadingChange?.(loading)
    },
    [onQueueLoadingChange]
  )

  useEffect(() => {
    return () => {
      setQueueLoading(false)
    }
  }, [setQueueLoading])

  const publishQueueStats = useCallback(() => {
    const pending = quickQueuePendingCountRef.current
    const total = quickQueueTotalCountRef.current
    const completed = Math.max(0, total - pending)
    const percent =
      total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0
    const running = quickQueueRunningRef.current
    const cancelRequested = quickQueueCancelRequestedRef.current
    const visible = pending > 0 || running

    onQueueStatsChange?.({
      visible,
      pending,
      total,
      completed,
      percent,
      running,
      cancelRequested,
    })
    setQueueLoading(visible)
    forceQueueStatsRender((v) => (v + 1) % 1000)
  }, [onQueueStatsChange, setQueueLoading])

  const cancelQuickQueue = useCallback(() => {
    if (
      quickQueuePendingCountRef.current === 0 &&
      !quickQueueRunningRef.current
    ) {
      return
    }

    quickQueueCancelRequestedRef.current = true
    publishQueueStats()
  }, [publishQueueStats])

  useEffect(() => {
    onQueueCancelRegister?.(cancelQuickQueue)
  }, [cancelQuickQueue, onQueueCancelRegister])

  const enqueueQuickGridMutation = useCallback(
    (runMutation: () => Promise<unknown>) => {
      const queueToken = ++quickAddLatestTokenRef.current
      quickQueuePendingCountRef.current += 1
      quickQueueTotalCountRef.current += 1

      if (
        !quickQueueRunningRef.current &&
        quickQueuePendingCountRef.current === 1
      ) {
        quickQueueCancelRequestedRef.current = false
      }

      publishQueueStats()

      quickAddQueueRef.current = quickAddQueueRef.current
        .catch(() => undefined)
        .then(async () => {
          if (quickQueueCancelRequestedRef.current) {
            return
          }

          quickQueueRunningRef.current = true
          publishQueueStats()

          await runMutation()
          quickAddNeedsRefetchRef.current = true

          quickQueueRunningRef.current = false
          publishQueueStats()
        })
        .finally(async () => {
          quickQueueRunningRef.current = false
          quickQueuePendingCountRef.current = Math.max(
            0,
            quickQueuePendingCountRef.current - 1
          )

          const queueIsDrained =
            quickQueuePendingCountRef.current === 0 &&
            !quickQueueRunningRef.current

          if (queueToken === quickAddLatestTokenRef.current && queueIsDrained) {
            if (quickAddNeedsRefetchRef.current) {
              quickAddNeedsRefetchRef.current = false
              await Promise.resolve(onRefetch?.())
            }

            quickQueueCancelRequestedRef.current = false
            quickQueueTotalCountRef.current = 0
          }

          publishQueueStats()
        })
        .catch(() => undefined)
    },
    [onRefetch, publishQueueStats]
  )

  const handleQuickAddShift = useCallback(
    (
      cellId: CellId,
      shiftTypeId: number,
      startTime?: string | null,
      endTime?: string | null
    ) => {
      const payload = {
        shiftTypeId,
        contractId: getContractId(cellId),
        date: getDay(cellId),
        startTime: startTime ?? null,
        endTime: endTime ?? null,
        shiftNote: null,
      }

      // Drag-paint quick-add can enqueue many cells very quickly.
      // Execute AddShift calls strictly one-by-one to avoid nested backend transactions.
      enqueueQuickGridMutation(() =>
        quickAddShift.mutateAsync({ data: payload })
      )
    },
    [enqueueQuickGridMutation, quickAddShift]
  )

  const handleQuickDeleteShift = useCallback(
    (cellId: CellId) => {
      const cell = items.cellsById?.[cellId]
      if (!cell) {
        return
      }

      const uniqueDeletes = new Set<string>()
      const deleteRequests: Array<{ dutyRosterId: number; shiftId: number }> =
        []

      for (const dutyRoster of [
        ...(cell.dutyRosters ?? []),
        ...(cell.overflowDutyRosters ?? []),
      ]) {
        if (!dutyRoster.dutyRosterId) {
          continue
        }

        for (const shift of dutyRoster.shifts ?? []) {
          if (!shift.shiftId) {
            continue
          }

          const shiftSlotId = getShiftSlotId(shift.shiftId)
          const key = `${dutyRoster.dutyRosterId}-${shiftSlotId}`
          if (uniqueDeletes.has(key)) {
            continue
          }

          uniqueDeletes.add(key)
          deleteRequests.push({
            dutyRosterId: dutyRoster.dutyRosterId,
            shiftId: shiftSlotId,
          })
        }
      }

      for (const request of deleteRequests) {
        enqueueQuickGridMutation(() =>
          quickDeleteShift.mutateAsync({ data: request })
        )
      }

      for (const absence of cell.absences ?? []) {
        if (!absence.personStatusId) {
          continue
        }

        const personStatusId = absence.personStatusId
        if (queuedPersonStatusDeleteIdsRef.current.has(personStatusId)) {
          continue
        }

        queuedPersonStatusDeleteIdsRef.current.add(personStatusId)
        enqueueQuickGridMutation(async () => {
          try {
            await quickDeletePersonStatus.mutateAsync({ personStatusId })
          } catch (error) {
            queuedPersonStatusDeleteIdsRef.current.delete(personStatusId)
            throw error
          }
        })
      }
    },
    [
      enqueueQuickGridMutation,
      items.cellsById,
      quickDeletePersonStatus,
      quickDeleteShift,
    ]
  )

  const resolveVerticalScrollElement = useCallback(() => {
    let current = gridRef.current?.parentElement ?? null
    while (current) {
      const style = window.getComputedStyle(current)
      const canScrollY =
        style.overflowY === "auto" || style.overflowY === "scroll"
      if (canScrollY) {
        return current
      }
      current = current.parentElement
    }

    return document.scrollingElement as HTMLElement | null
  }, [])

  useEffect(() => {
    horizontalScrollRef.current = gridRef.current?.parentElement ?? null
    verticalScrollRef.current = resolveVerticalScrollElement()
  }, [resolveVerticalScrollElement])

  useEffect(() => {
    rowExpandedStatesRef.current = rowExpandedStates
  }, [rowExpandedStates])

  useEffect(() => {
    const m: Record<string, boolean> = {}
    function createKeys(row: RowHeader, parentId: string | null) {
      const rowId = parentId ? `${parentId}/${row.id}` : row.id!.toString()
      m[rowId] = row.opened ?? false
      ;(row.children as RowHeader[])?.forEach((v) => createKeys(v, rowId))
    }
    items.rowHeaders!.forEach((v) => createKeys(v, null))
    const prev = rowExpandedStatesRef.current
    const prevKeys = Object.keys(prev)
    const nextKeys = Object.keys(m)
    if (prevKeys.length === nextKeys.length) {
      let changed = false
      for (const key of nextKeys) {
        if (prev[key] !== m[key]) {
          changed = true
          break
        }
      }

      if (!changed) {
        return
      }
    }

    setRowExpandedStates(m)
  }, [items.rowHeaders, setRowExpandedStates])

  const [headerSize, _] = focus === "cast-planning" ? [20, 68] : cellSizes["sm"]
  const hasAnyActivity = useMemo(
    () =>
      store.columns.some(
        (column) => column.type === "day-col" && !!column.activity
      ),
    [store.columns]
  )
  const hasActivityHeader =
    items.showActivityHeader && focus !== "absence-planning" && hasAnyActivity

  const gridTemplateColumns = store.gridTemplateColumns
  const { rowSizes, rowTypes } = useMemo(() => {
    const sizes: string[] = []
    const types: RowHeader["rowType"][] = []
    function addSizes(
      row: RowHeader,
      parentId: string | null,
      expanded: boolean,
      siblings: RowHeader[]
    ) {
      const omitGroupingRow = isOnlyGroupingRow(row, siblings)
      if (!omitGroupingRow) {
        sizes.push(expanded ? `${headerSize}px` : "0")
        types.push(row.rowType)
      }
      if (row.children && row.children.length > 0) {
        const id = parentId ? `${parentId}/${row.id}` : row.id!.toString()
        const opened = rowExpandedStates[id]
        const children = row.children as RowHeader[]
        children.forEach((v) => {
          addSizes(v, id, expanded && (omitGroupingRow || opened), children)
        })
      }
    }
    const rowHeaders = items.rowHeaders as RowHeader[]
    rowHeaders.forEach((v) => {
      addSizes(v, null, true, rowHeaders)
    })
    return { rowSizes: sizes, rowTypes: types }
  }, [headerSize, items.rowHeaders, rowExpandedStates])

  const gridTemplateRows = useMemo(
    () =>
      `${headerSize}px ${headerSize}px ${headerSize}px ${hasActivityHeader ? "72px" : "0px"} ${rowSizes.join(" ")}`,
    [hasActivityHeader, headerSize, rowSizes]
  )
  const transposedGridTemplateColumns = useMemo(
    () =>
      `${headerSize}px ${headerSize}px ${headerSize}px ${hasActivityHeader ? "72px" : "0px"} ${rowSizes
        .map((size, index) =>
          size === "0"
            ? "0"
            : rowTypes[index] === "Department" || rowTypes[index] === "Workarea"
              ? "20px"
              : focus === "cast-planning"
                ? "70px"
                : size
        )
        .join(" ")}`,
    [focus, hasActivityHeader, headerSize, rowSizes, rowTypes]
  )
  const transposedGridTemplateRows = useMemo(() => {
    if (focus !== "cast-planning") {
      return gridTemplateColumns
    }

    const contentColumnOffset = 3 + store.formulas.length
    return gridTemplateColumns
      .split(" ")
      .map((track, index) =>
        store.columns[index - contentColumnOffset]?.type === "day-col"
          ? "20px"
          : track
      )
      .join(" ")
  }, [focus, gridTemplateColumns, store.columns, store.formulas.length])

  const colHeaderHeight = 4
  const contentStartRow = colHeaderHeight + 1

  const getColumnSize = React.useCallback(
    (col: (typeof store.columns)[number]) => {
      if (col?.type === "day-col") {
        if (focus === "cast-planning") {
          return orientation === "normal" ? 70 : 20
        }
        if (focus === "absence-planning") {
          return col.colId === 0 ? 20 : 0
        }
        return 20
      }
      if (col?.type === "day") {
        return focus === "absence-planning" ? 0 : 4
      }
      return 0
    },
    [focus, orientation]
  )

  const virtualizableColumnIndices = useMemo(() => {
    const indices: number[] = []
    for (let i = 0; i < store.columns.length; i++) {
      if (getColumnSize(store.columns[i]) > 0) {
        indices.push(i)
      }
    }
    return indices
  }, [store.columns, focus, orientation])

  const getRowVirtualSize = useCallback(
    (index: number) => {
      const raw = rowSizes[index]
      const parsed = Number.parseInt(raw, 10)
      if (Number.isNaN(parsed)) {
        return headerSize
      }

      if (
        orientation === "transpose" &&
        (rowTypes[index] === "Department" || rowTypes[index] === "Workarea")
      ) {
        return parsed > 0 ? 20 : 0
      }

      return orientation === "transpose" &&
        focus === "cast-planning" &&
        parsed > 0
        ? 70
        : parsed
    },
    [focus, headerSize, orientation, rowSizes, rowTypes]
  )

  const rowVirtualizer = useVirtualizer({
    horizontal: orientation === "transpose",
    count: rowSizes.length,
    getScrollElement: () =>
      orientation === "transpose"
        ? horizontalScrollRef.current
        : verticalScrollRef.current,
    estimateSize: getRowVirtualSize,
    overscan: 12,
  })

  const columnVirtualizer = useVirtualizer({
    horizontal: orientation === "normal",
    count: virtualizableColumnIndices.length,
    getScrollElement: () =>
      orientation === "transpose"
        ? verticalScrollRef.current
        : horizontalScrollRef.current,
    estimateSize: (index) =>
      getColumnSize(store.columns[virtualizableColumnIndices[index]]),
    overscan: 10,
  })

  useEffect(() => {
    rowVirtualizer.measure()
  }, [focus, orientation, items.rowCount, rowSizes, rowVirtualizer])

  useEffect(() => {
    columnVirtualizer.measure()
  }, [
    orientation,
    store.columns.length,
    focus,
    virtualizableColumnIndices.length,
  ])

  const virtualItems = rowVirtualizer.getVirtualItems()
  const virtualColumnItems = columnVirtualizer.getVirtualItems()
  const visibleGridRows = useMemo(() => {
    if (virtualItems.length === 0) {
      const rows = new Set<number>()
      for (let row = store.window.startRow; row <= store.window.endRow; row++) {
        rows.add(row)
      }
      return rows
    }

    return new Set(virtualItems.map((item) => item.index + contentStartRow))
  }, [
    contentStartRow,
    store.window.endRow,
    store.window.startRow,
    virtualItems,
  ])

  const visibleDataColumns = useMemo(() => {
    const visible = new Set<number>()

    if (orientation === "normal") {
      // On very wide normal grids, retain the Zustand horizontal scroll
      // window while the browser virtualizer catches up.
      const windowStart = Math.max(0, store.window.startCol - 2)
      const windowEnd = Math.min(store.columns.length - 1, store.window.endCol)
      for (let col = windowStart; col <= windowEnd; col++) {
        visible.add(col)
      }
    }

    if (
      virtualizableColumnIndices.length === 0 ||
      virtualColumnItems.length === 0
    ) {
      return Array.from(visible).sort((a, b) => a - b)
    }

    for (const item of virtualColumnItems) {
      const sourceIndex = item.index
      for (let offset = -2; offset <= 2; offset++) {
        const i = sourceIndex + offset
        if (i < 0 || i >= virtualizableColumnIndices.length) continue
        visible.add(virtualizableColumnIndices[i])
      }
    }

    return Array.from(visible).sort((a, b) => a - b)
  }, [
    orientation,
    store.columns.length,
    store.window.endCol,
    store.window.startCol,
    virtualColumnItems,
    virtualizableColumnIndices,
  ])

  const headerColRange = useMemo(() => {
    if (visibleDataColumns.length === 0) {
      return {
        start: Math.max(0, store.window.startCol - 10),
        end: Math.min(store.columns.length - 1, store.window.endCol + 10),
      }
    }

    const start = visibleDataColumns[0]
    const end = visibleDataColumns[visibleDataColumns.length - 1]
    return {
      start: Math.max(0, start - 10),
      end: Math.min(store.columns.length - 1, end + 10),
    }
  }, [
    store.columns.length,
    store.window.endCol,
    store.window.startCol,
    visibleDataColumns,
  ])

  const visibleDayColumns = useMemo(() => {
    const dayColumns: Array<{ col: number; date: DateOnly; colId: number }> = []
    for (const col of visibleDataColumns) {
      if (col < 0 || col >= store.columns.length) continue
      const entry = store.columns[col]
      if (entry.type !== "day-col") continue
      dayColumns.push({ col, date: entry.date, colId: entry.colId })
    }
    return dayColumns
  }, [store.columns, visibleDataColumns])

  const { colorScheme } = useMantineColorScheme()

  const maxDepth = store.headerColumnCount + 1
  const overlapsHeaderRange = (startIndex: number, span: number) => {
    const endIndex = startIndex + Math.max(1, span) - 1
    return endIndex >= headerColRange.start && startIndex <= headerColRange.end
  }
  const isHeaderIndexVisible = (index: number) =>
    index >= headerColRange.start && index <= headerColRange.end
  const departmentId = useSelectedDepartmentIds(departmentTreeNodes)[0]
  const columnHeaderCells = useMemo(() => {
    // TODO: remove magic numbers
    const transform = orientation === "normal" ? undefined : "rotate(-90deg)"
    const cells: React.ReactElement[] = []
    for (let i = 0; i < store.columns.length; i++) {
      const c = store.columns[i]
      match(c)
        .with({ type: "month" }, (x) => {
          if (!overlapsHeaderRange(i, x.span - 2)) return
          cells.push(
            <div
              key={`col-month-${i}`}
              className={clsx(styles["cell"], styles["header"])}
              style={orientGridStyle(orientation, {
                top: 0,
                gridColumn: `${i + maxDepth + 2 + (i === 0 ? +1 : 0)} / span ${x.span - 2}`,
                gridRow: 1,
              })}
            >
              <Text style={{ transform }} size="xs">
                {dayjs(x.date).format("MMM")}
              </Text>
            </div>
          )
        })
        .with({ type: "day" }, (x) => {
          if (!overlapsHeaderRange(i, x.span - 1)) return
          const isLightBackgroundColor = x.bg && isLightColor(x.bg)
          cells.push(
            <div
              key={`col-day-header-${i}`}
              className={clsx(styles["cell"], styles["header"])}
              style={orientGridStyle(orientation, {
                top: 36,
                background:
                  colorScheme === "dark" && x.bg ? alpha(x.bg, 0.7) : x.bg,
                gridColumn: `${i + maxDepth + 1} / span ${x.span - 1}`,
                gridRow: 3,
              })}
            >
              <Text
                style={{ transform, whiteSpace: "nowrap" }}
                size="xs"
                c={isLightBackgroundColor ? "black" : undefined}
              >
                {focus === "absence-planning" || x.span - 1 === 1
                  ? dayjs(x.date).format("DD")
                  : dayjs(x.date).format("dd DD")}
              </Text>
            </div>
          )
          if (!isHeaderIndexVisible(i)) return
          cells.push(
            <div
              key={`col-day-separator-${i}`}
              style={orientGridStyle(orientation, {
                gridColumn: `${i + maxDepth} `,
                gridRow: 4,
                background: "transparent",
              })}
            ></div>
          )
        })
        .with({ type: "day-col" }, (x) => {
          if (!hasActivityHeader) return
          if (!isHeaderIndexVisible(i)) return
          cells.push(
            <div
              key={`col-day-col-${i}`}
              className={clsx(styles["header"], styles["activity-cell"])}
              style={orientGridStyle(orientation, {
                top: 54,
                textAlign: "start",
                gridColumn: `${i + maxDepth} / span 1`,
                gridRow: 4,
                overflow: "hidden",
              })}
            >
              {x.activity && departmentId !== undefined ? (
                <SuspenseWithErrorBoundary>
                  <ActivityHeaderCell
                    activity={x.activity}
                    departmentId={departmentId}
                  />
                </SuspenseWithErrorBoundary>
              ) : null}
            </div>
          )
        })
        .with({ type: "week" }, (x) => {
          if (!overlapsHeaderRange(i, x.span - 2)) return
          cells.push(
            <div
              key={`col-week-header-${i}`}
              className={clsx(
                styles["cell"],
                styles["header"],
                dayjs(x.date).isoWeek() % 2 === 0 && styles["header-alt"]
              )}
              style={orientGridStyle(orientation, {
                top: 18,
                gridRow: 2,
                gridColumn: `${i + maxDepth + 2} / span ${x.span - 2}`,
              })}
            >
              <Text
                textWrap={orientation === "transpose" ? "nowrap" : undefined}
                style={{ transform }}
                size="xs"
              >
                KW {dayjs(x.date).isoWeek()}
              </Text>
            </div>
          )
          if (!isHeaderIndexVisible(i)) return
          cells.push(
            <div
              key={`col-week-separator-${i}`}
              style={orientGridStyle(orientation, {
                gridColumn: `${i + maxDepth}`,
                gridRow: 3,
              })}
            />
          )
        })
    }
    return cells
  }, [
    colorScheme,
    departmentId,
    focus,
    hasActivityHeader,
    headerColRange.end,
    headerColRange.start,
    maxDepth,
    store.columns,
    orientation,
  ])

  const formulaHeaderCells = useMemo(() => {
    const cells: React.ReactElement[] = []
    const offset = store.headerColumnCount - store.formulas.length + 1
    for (let col = 0; col < store.formulas.length; col++) {
      cells.push(
        <FormulaHeaderCell
          orientation={orientation}
          key={`formula-header-${col}`}
          formula={store.formulas[col]}
          style={{
            zIndex: 130,
            position: "sticky",
            ...orientGridStyle(orientation, {
              gridRow: `1 / span ${colHeaderHeight}`,
              gridColumn: `${offset + col} / span 1`,
              top: orientation === "transpose" ? 0 : undefined,
              left: formulaTrackOffset + col * formulaTrackWidth,
            }),
          }}
        />
      )
    }
    return cells
  }, [colHeaderHeight, store.formulas, store.headerColumnCount, orientation])
  const { rowHeaderCells, rowCells } = useMemo(() => {
    const assembledRowHeaderCells: React.ReactElement[] = []
    const assembledRowCells: React.ReactElement[] = []

    function recCreateRowheaders(
      header: RowHeader,
      parentHierarchicalId: string | null,
      gridRow: number,
      depth: number,
      siblings: RowHeader[],
      omittedDepth: number = 0
    ) {
      const hierarchicalRowId =
        parentHierarchicalId !== null
          ? parentHierarchicalId + "/" + header.id!
          : header.id!.toString()

      const expanded = rowExpandedStates[hierarchicalRowId] ?? true
      const omitGroupingRow = isOnlyGroupingRow(header, siblings)
      const isRowInWindow = !omitGroupingRow && visibleGridRows.has(gridRow)

      if (isRowInWindow) {
        assembledRowHeaderCells.push(
          <div
            id={hierarchicalRowId || ""}
            key={hierarchicalRowId || ""}
            className={clsx(
              styles["cell"],
              styles["header"],
              header.rowType === "Contract" && styles["row-header-contract"],
              header.rowType !== "Contract" && styles["row-header"],
              header.highlight && styles["header-highlighted"]
            )}
            style={{
              ...orientGridStyle(orientation, {
                gridRow,
                gridColumn: `${depth + 1} / span ${header.depth! + omittedDepth + 1 + (header.children?.length === 0 ? 1 : 0)}`,
                left: depth * 5,
              }),
              justifyContent: orientation === "transpose" ? "center" : "start",
              paddingLeft: orientation === "transpose" ? 0 : 8,
              flexDirection: orientation === "transpose" ? "column" : "row",
            }}
          >
            {header!.children && header.children.length > 0 ? (
              <ActionIcon
                style={{
                  visibility:
                    header!.children && header.children.length > 0
                      ? undefined
                      : "hidden",
                  position:
                    orientation === "transpose" ? "absolute" : undefined,
                  bottom: orientation === "transpose" ? 0 : undefined,
                  left: orientation === "transpose" ? "50%" : undefined,
                  transform:
                    orientation === "transpose"
                      ? "rotate(-90deg) translateY(-50%)"
                      : undefined,
                }}
                size="xs"
                variant="subtle"
                color="gray"
                onMouseDown={() => {
                  setRowExpandedStates((prev) => ({
                    ...prev,
                    [hierarchicalRowId]: !expanded,
                  }))
                }}
              >
                {expanded ? <IconChevronDown /> : <IconChevronRight />}
              </ActionIcon>
            ) : null}
            <Tooltip
              label={header.tooltip}
              events={{
                hover: Boolean(header.tooltip),
                focus: false,
                touch: false,
              }}
            >
              <Text
                size="xs"
                style={{
                  whiteSpace: "nowrap",
                  // overflow: orientation === "transpose" ? "hidden" : "hidden",
                  transform:
                    orientation === "transpose" ? "rotate(-90deg)" : undefined,
                }}
              >
                {header.label?.startsWith("@")
                  ? t(header.label.substring(1) as any)
                  : header.label}
              </Text>
            </Tooltip>
          </div>
        )
      }

      const offset = store.headerColumnCount - store.formulas.length + 1
      if (isRowInWindow) {
        for (let col = 0; col < store.formulas.length; col++) {
          const contractId =
            header.rowType === "Contract" ? (header.id ?? null) : null
          assembledRowHeaderCells.push(
            <FormulaCell
              key={`${hierarchicalRowId}-formula-${col}`}
              formula={store.formulas[col]}
              contractId={contractId}
              className={clsx(styles["cell"])}
              style={orientGridStyle(orientation, {
                position: "sticky",
                left: formulaTrackOffset + col * formulaTrackWidth,
                zIndex: 120,
                background:
                  "light-dark(var(--mantine-color-gray-0),var(--mantine-color-dark-6))",
                gridRow,
                gridColumn: `${offset + col} / span 1`,
              })}
            />
          )
        }

        for (const dayCol of visibleDayColumns) {
          const cellId = getCellId(hierarchicalRowId, dayCol.date, dayCol.colId)
          const cell = items.cellsById![cellId]
          const props = {
            row: gridRow,
            col: dayCol.col,
            id: cellId,
            className: clsx(styles["cell"]),
            style: orientGridStyle(orientation, {
              gridRow,
              gridColumn: `${maxDepth + dayCol.col} / span ${cell?.span ?? 1}`,
            }),
          }
          assembledRowCells.push(
            <MenuCellRenderer
              key={cellId}
              containerProps={props}
              cell={cell}
              cellId={cellId}
              header={header}
              onQuickAddShift={handleQuickAddShift}
              onQuickDeleteShift={handleQuickDeleteShift}
            />
          )
        }
      }

      let row = gridRow + (omitGroupingRow ? 0 : 1)
      if (omitGroupingRow || expanded) {
        const children = header.children as RowHeader[]
        children?.forEach((v) => {
          recCreateRowheaders(
            v,
            hierarchicalRowId,
            row,
            omitGroupingRow ? depth : depth + 1,
            children,
            omitGroupingRow ? omittedDepth + 1 : omittedDepth
          )
          row += getGroupingTrackSpan(v, children)
        })
      }
    }

    let row = colHeaderHeight + 1
    const rowHeaders = items.rowHeaders as RowHeader[]
    rowHeaders.forEach((v) => {
      recCreateRowheaders(v, null, row, 0, rowHeaders)
      row += getGroupingTrackSpan(v, rowHeaders)
    })

    return {
      rowHeaderCells: assembledRowHeaderCells,
      rowCells: assembledRowCells,
    }
  }, [
    colHeaderHeight,
    items.cellsById,
    items.rowHeaders,
    maxDepth,
    rowExpandedStates,
    setRowExpandedStates,
    store.formulas,
    store.headerColumnCount,
    t,
    handleQuickAddShift,
    handleQuickDeleteShift,
    visibleDayColumns,
    visibleGridRows,
  ])

  const start = 5
  const height = items.rowCount

  const overlayAreas = useMemo(
    () =>
      items.overlays?.map((overlay, i) => {
        const { area } = overlay
        const fromI = store.columns.findIndex(
          (v) => v.type === "day" && v.date === overlay.area.from
        )
        const untilI = store.columns.findIndex(
          (v) => v.type === "day" && v.date === overlay.area.until
        )
        const width = untilI - fromI + 1
        return (
          <div key={`overlay-${i}`}>
            <div
              style={orientGridStyle(orientation, {
                gridColumn: ` ${fromI + start} / span ${width} `,
                gridRow: area.rowNumber
                  ? start + area.rowNumber
                  : `${start} / span ${height}`,

                pointerEvents: "none",
                zIndex: 10,
                ...(overlay.style === "Dimmed"
                  ? {
                      background:
                        "light-dark(var(--mantine-color-gray-4),var(--mantine-color-dark-8))",
                      opacity: 0.5,
                    }
                  : overlay.style === "Highlighted"
                    ? {
                        border:
                          " 2px dashed light-dark(var(--mantine-color-blue-8),var(--mantine-color-white))",
                      }
                    : undefined),
              })}
            />
          </div>
        )
      }),
    [height, items.overlays, orientation, start, store.columns]
  )

  return (
    <div
      ref={gridRef}
      style={{
        display: "grid",
        gridTemplateColumns:
          orientation === "normal"
            ? gridTemplateColumns
            : transposedGridTemplateColumns,
        gridTemplateRows:
          orientation === "normal"
            ? gridTemplateRows
            : transposedGridTemplateRows,
        // this is important, hidden rows get height 0. if we would allow gaps then the hidden rows would also take space
        gap: 0,
        position: "relative",
        width: "max-content",
      }}
      className={clsx(hoverEffect && styles["hover-enabled"])}
    >
      <div
        style={orientGridStyle(orientation, {
          gridColumn: `1 / span ${maxDepth - store.formulas.length - 1}`,
          gridRow: `1 / span ${colHeaderHeight}`,
          position: "sticky",
          background:
            "light-dark(var(--mantine-color-white),var(--mantine-color-dark-7))",
          top: 0,
          left: 0,
          zIndex: 150,
        })}
      >
        {title}
        <ActionIcon
          size="xs"
          variant="light"
          color="gray"
          onClick={() =>
            setOrientation((current) =>
              current === "normal" ? "transpose" : "normal"
            )
          }
        >
          <IconArrowsDoubleSwNe size={14} />
        </ActionIcon>
      </div>

      {columnHeaderCells}

      {formulaHeaderCells}

      {rowHeaderCells}

      <div
        style={orientGridStyle(orientation, {
          gridColumn: `1 / span ${maxDepth - 1}`,
          gridRow: `${contentStartRow} / span ${items.rowCount}`,
          position: "sticky",
          left: 0,
          zIndex: 90,
          pointerEvents: "none",
          background:
            "light-dark(var(--mantine-color-gray-0),var(--mantine-color-dark-6))",
        })}
      />

      {rowCells}

      {overlayAreas}

      <Modals onRefetch={onRefetch} />
    </div>
  )
}

function Modals({ onRefetch }: { onRefetch?: () => void }) {
  const { t } = useTranslation()

  const { modal, setModal, getActivity } = useGridStore(
    useShallow((s) => ({
      modal: s.modalState,
      setModal: s.setModalState,
      getActivity: s.getActivity,
    }))
  )

  return (
    <Drawer
      opened={modal !== null}
      onClose={() => {
        setModal(null)
      }}
      title={
        <Tooltip label={modal?.view ?? ""}>
          <Title order={3}>
            {modal
              ? match(modal.view)
                  .with("add-absence", () => t("EnterAbsence"))
                  .with("add-personstatus-absence", () => t("EnterAbsence"))
                  .with("add-shift", () => t("AddShift"))
                  .with("edit-personstatus-absence", () => t("Absence"))
                  .with("edit-absence", () => t("Absence"))
                  .with("edit-absence-shift", () => t("Absence"))
                  .with("edit-shift", () => t("EditShift"))
                  .with("add-cast", () => t("AddCast"))
                  .with("edit-cast", () => t("EditCast"))
                  .with("add-personstatus-shift", () => t("AddShift"))
                  .exhaustive()
              : null}
          </Title>
        </Tooltip>
      }
      position="right"
      transitionProps={{ duration: 150 }}
      overlayProps={{ backgroundOpacity: 0.2, blur: 0 }}
    >
      <SuspenseWithFallback>
        {modal?.view === "add-personstatus-shift" ? (
          <AddPersonStatusShiftModalContent
            departmentId={getDepartmentId(modal.cellId)}
            shiftTypeId={modal.shiftTypeId}
            date={getDay(modal.cellId)}
            contractId={getContractId(modal.cellId)}
            onCancel={() => setModal(null)}
            onAdded={() => {
              onRefetch && onRefetch()
              setModal(null)
            }}
          />
        ) : null}
        {modal?.view === "add-personstatus-absence" ? (
          <AddAbsenceModal
            coordinate={{
              type: "absence-cell",
              contractId: getContractId(modal.cellId),
              shiftTypeId: modal.shiftType.shiftTypeId,
              date: getDay(modal.cellId),
            }}
            departmentId={getDepartmentId(modal.cellId)}
            onAdded={() => {
              onRefetch && onRefetch()
              setModal(null)
            }}
            onCancel={() => setModal(null)}
          />
        ) : null}
        {modal?.view === "add-shift" ? (
          <CreateOrEditShiftForm
            type="create-with-shift-type-select"
            shiftTypeId={modal.shiftType?.shiftTypeId}
            departmentId={getDepartmentId(modal.cellId)}
            date={getDay(modal.cellId)}
            contractId={getContractId(modal.cellId)}
            onClose={() => setModal(null)}
            onSuccess={() => {
              onRefetch && onRefetch()
              setModal(null)
            }}
            opened={modal?.view === "add-shift"}
          />
        ) : null}
        {modal?.view === "add-absence" ? (
          <CreateOrEditShiftForm
            type="create"
            shiftTypeId={modal.shiftType.shiftTypeId}
            date={getDay(modal.cellId)}
            contractId={getContractId(modal.cellId)}
            onClose={() => setModal(null)}
            onSuccess={() => {
              onRefetch && onRefetch()
              setModal(null)
            }}
            opened={false}
          />
        ) : null}
        {modal?.view === "edit-shift" ||
        modal?.view === "edit-absence-shift" ? (
          <CreateOrEditShiftForm
            type="edit"
            key={modal.view}
            dutyRosterId={modal.dutyRosterId}
            contractId={getContractId(modal.cellId)}
            date={getDay(modal.cellId)}
            shiftId={modal.shiftId}
            onSuccess={() => {
              setModal(null)
              onRefetch && onRefetch()
            }}
            onClose={() => {
              setModal(null)
            }}
            opened={
              modal?.view === "edit-shift" ||
              modal?.view === "edit-absence-shift"
            }
          />
        ) : null}
        {modal?.view === "edit-personstatus-absence" ? (
          <EditAbsenceContent
            departmentId={getDepartmentId(modal.cellId)}
            // TODO
            personStatus={
              {
                ...modal.personStatus,
                startDate: modal.personStatus.startDate,
                endDate: modal.personStatus.endDate,
                contractId: getContractId(modal.cellId),
                personId: modal.personStatus.personId!,
                personStatusId: modal.personStatus.personStatusId!,
                startTime: modal.personStatus.startTime || null,
                endTime: modal.personStatus.endTime || null,
                shiftTypeId: modal.personStatus.shiftTypeId!,
                statusDisplayName: modal.personStatus.name!,
                statusTypPrintColors: null,
                note: modal.personStatus.note ?? "",
              } as any
            }
            onEdited={() => {
              setModal(null)
              onRefetch && onRefetch()
            }}
            onCancel={() => {
              setModal(null)
            }}
          />
        ) : null}
        {modal?.view === "add-cast" ? (
          <AddCastModal
            departmentId={getDepartmentId(modal.cellId)}
            coordinate={{
              type: "activity-cell",
              contractId: getContractId(modal.cellId),
              activityId: getActivity(modal.cellId)?.activityId!,
              workareaId: getWorkareaId(modal.cellId),
            }}
            onAdded={() => {
              onRefetch && onRefetch()
              setModal(null)
            }}
            onCancel={() => {
              setModal(null)
            }}
          />
        ) : null}
        {modal?.view === "edit-cast" ? (
          <EditCastModal
            coordinate={{
              type: "edit",
              contractId: getContractId(modal.cellId),
              activityId: getActivity(modal.cellId)?.activityId!,
              workareaId: getWorkareaId(modal.cellId),
              castId: modal.castId,
            }}
            onCancel={() => {
              setModal(null)
            }}
            onDeleted={() => {
              setModal(null)
              onRefetch && onRefetch()
            }}
            onEdited={() => {
              setModal(null)
              onRefetch && onRefetch()
            }}
          />
        ) : null}
      </SuspenseWithFallback>
    </Drawer>
  )
}
